import * as React from 'react'
import clsx from 'clsx'
import { ListContext } from '../List/ListContext'
import { ButtonBase, ButtonBaseProps } from '../ButtonBase'
import { useEnhancedEffect, useForkRef } from '../../hooks'
import { SxProps, sx as sxFunc } from '../sx'
import styles from './index.module.scss'
import { capitalize } from '../../utils'
import { MenuListProps } from '../MenuList'

export interface MenuItemClasses {
  /** Styles applied to the root element. */
  root: string
  /** State class applied to the root element if keyboard focused. */
  focusVisible: string
  /** State class applied to the root element if `disabled={true}`. */
  disabled: string
  /** State class applied to the root element if `selected={true}`. */
  selected: string
  /** Styles applied to the startIcon element if supplied. */
  startIcon: string
  /** Styles applied to the children element. */
  content: string
  /** Styles applied to the endIcon element if supplied. */
  endIcon: string
  /** Styles applied to the root element if `size="small"`. */
  sizeSmall: string
  /** Styles applied to the root element if `size="medium"`. */
  sizeMedium: string
  /** Styles applied to the root element if `size="large"`. */
  sizeLarge: string
}

export interface MenuItemProps
  extends ButtonBaseProps,
    Pick<MenuListProps, 'size'> {
  /**
   * If `true`, the list item is focused during the first mount.
   * Focus will also be triggered if the value changes from false to true.
   * @default false
   */
  autoFocus?: boolean
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<MenuItemClasses>
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   * @default li
   */
  Component?: React.ElementType
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled?: boolean
  /**
   * Use to apply selected styling.
   * @default false
   */
  selected?: boolean
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * @ignore Internal prop
   */
  value?: any
  /**
   * Element placed before the children.
   */
  startIcon?: JSX.Element
  /**
   * Element placed after the children.
   */
  endIcon?: JSX.Element
}

export const MenuItem = React.forwardRef<HTMLButtonElement, MenuItemProps>(
  function MenuItem(props, ref) {
    const {
      autoFocus = false,
      Component = 'li',
      classes,
      focusVisibleClassName,
      tabIndex: tabIndexProp,
      className,
      disabled,
      selected,
      sx,
      value: _,
      children,
      startIcon: startIconProp,
      endIcon: endIconProp,
      size = 'medium',
      ...other
    } = props

    const context = React.useContext(ListContext)
    const childContext = React.useMemo(
      () => ({
        size: size !== 'medium' ? size : context.size,
      }),
      [context.size, size]
    )

    const menuItemRef = React.useRef(null)
    useEnhancedEffect(() => {
      if (autoFocus) {
        if (menuItemRef.current) {
          // @ts-ignore
          menuItemRef.current?.focus()
        } else if (process.env.NODE_ENV !== 'production') {
          console.error(
            'Unable to set focus to a MenuItem whose component has not been rendered.'
          )
        }
      }
    }, [autoFocus])

    const handleRef = useForkRef(menuItemRef, ref)

    let tabIndex
    if (!props.disabled) {
      tabIndex = tabIndexProp !== undefined ? tabIndexProp : -1
    }

    const startIcon = startIconProp && (
      <span
        className={clsx(
          styles.startIcon,
          classes?.startIcon,
          childContext.size && styles[childContext.size]
        )}
      >
        {startIconProp}
      </span>
    )

    const endIcon = endIconProp && (
      <span
        className={clsx(
          styles.endIcon,
          classes?.endIcon,
          childContext.size && styles[childContext.size]
        )}
      >
        {endIconProp}
      </span>
    )

    const content = children && (
      <span className={clsx(styles.content, classes?.content)}>{children}</span>
    )

    return (
      <ListContext.Provider value={childContext}>
        <ButtonBase
          Component={Component}
          ref={handleRef}
          role="menuitem"
          tabIndex={tabIndex}
          focusVisibleClassName={clsx(
            styles['focus-visible'],
            classes?.focusVisible,
            focusVisibleClassName
          )}
          className={clsx(
            styles.root,
            classes?.root,
            selected && [styles.selected, classes?.selected],
            disabled && [styles.disabled, classes?.disabled],
            childContext.size && [
              styles[childContext.size],
              //@ts-ignore
              classes?.[`size${capitalize(childContext.size)}`],
            ],
            className,
            sxFunc(sx)
          )}
          disabled={disabled}
          {...other}
          classes={classes}
        >
          {startIcon}
          {content}
          {endIcon}
        </ButtonBase>
      </ListContext.Provider>
    )
  }
)
